export type ConsoleRegion = 'EU' | 'US' | 'ME'

const config = {
	development: {
		agents: null,
		analyticsHost: null,
		bridge: 'http://localhost:8085/v1',
		core: 'http://localhost:8083/v1',
		marketing: 'http://localhost:8081/v1',
		metabaseHost: null,
		micrositeDomain: null,
		receipts: 'http://localhost:8082/v1',
		rewards: 'http://localhost:8080/v1',
	},
	production: {
		EU: {
			agents: 'https://agents.wlloyalty.net',
			analyticsHost: 'https://reports.wlloyalty.net',
			bridge: 'https://api.bridge.wlloyalty.net/v1',
			core: 'https://api.core.wlloyalty.net/v1',
			intelligence: 'https://api.intelligence.wlloyalty.net/v1',
			marketing: 'https://api.marketing.wlloyalty.net/v1',
			metabaseHost: 'https://analytics.wlloyalty.net',
			micrositeDomain: 'web.wlloyalty.net',
			receipts: 'https://api.receipts.wlloyalty.net/v1',
			rewards: 'https://api.rewards.wlloyalty.net/v1',
		},
		ME: {
			agents: 'https://agents.me.wlloyalty.net',
			analyticsHost: 'https://reports.me.wlloyalty.net',
			bridge: 'https://api.bridge.me.wlloyalty.net/v1',
			core: 'https://api.core.me.wlloyalty.net/v1',
			intelligence: 'https://api.intelligence.me.wlloyalty.net/v1',
			marketing: 'https://api.marketing.me.wlloyalty.net/v1',
			metabaseHost: 'https://analytics.me.wlloyalty.net',
			micrositeDomain: 'web.me.wlloyalty.net',
			receipts: 'https://api.receipts.me.wlloyalty.net/v1',
			rewards: 'https://api.rewards.me.wlloyalty.net/v1',
		},
		US: {
			agents: 'https://agents.us.wlloyalty.net',
			analyticsHost: 'https://reports.us.wlloyalty.net',
			bridge: 'https://api.bridge.us.wlloyalty.net/v1',
			core: 'https://api.core.us.wlloyalty.net/v1',
			intelligence: 'https://api.intelligence.us.wlloyalty.net/v1',
			marketing: 'https://api.marketing.us.wlloyalty.net/v1',
			metabaseHost: 'https://analytics.us.wlloyalty.net',
			micrositeDomain: 'web.us.wlloyalty.net',
			receipts: 'https://api.receipts.us.wlloyalty.net/v1',
			rewards: 'https://api.rewards.us.wlloyalty.net/v1',
		},
	},
	staging: {
		EU: {
			agents: 'https://staging.agents.wlloyalty.net',
			analyticsHost: null,
			bridge: 'https://api.staging.bridge.wlloyalty.net/v1',
			core: 'https://api.staging.core.wlloyalty.net/v1',
			intelligence: 'https://api.staging.intelligence.wlloyalty.net/v1',
			marketing: 'https://api.staging.marketing.wlloyalty.net/v1',
			metabaseHost: null,
			micrositeDomain: 'staging.web.wlloyalty.net',
			receipts: 'https://api.staging.receipts.wlloyalty.net/v1',
			rewards: 'https://api.staging.rewards.wlloyalty.net/v1',
		},
		ME: {
			agents: 'https://staging.agents.me.wlloyalty.net',
			analyticsHost: null,
			bridge: 'https://api.staging.bridge.me.wlloyalty.net/v1',
			core: 'https://api.staging.core.me.wlloyalty.net/v1',
			intelligence: 'https://api.staging.intelligence.me.wlloyalty.net/v1',
			marketing: 'https://api.staging.marketing.me.wlloyalty.net/v1',
			metabaseHost: null,
			micrositeDomain: 'staging.web.me.wlloyalty.net',
			receipts: 'https://api.staging.receipts.me.wlloyalty.net/v1',
			rewards: 'https://api.staging.rewards.me.wlloyalty.net/v1',
		},
		US: {
			agents: 'https://staging.agents.us.wlloyalty.net',
			analyticsHost: null,
			bridge: 'https://api.staging.bridge.us.wlloyalty.net/v1',
			core: 'https://api.staging.core.us.wlloyalty.net/v1',
			intelligence: 'https://api.staging.intelligence.us.wlloyalty.net/v1',
			marketing: 'https://api.staging.marketing.us.wlloyalty.net/v1',
			metabaseHost: null,
			micrositeDomain: 'staging.web.us.wlloyalty.net',
			receipts: 'https://api.staging.receipts.us.wlloyalty.net/v1',
			rewards: 'https://api.staging.rewards.us.wlloyalty.net/v1',
		},
	},
}

// Handle mode query parameter to set environment before rendering.
// This only matters for externally-hosted tenants, where live/test share the
// same slug and the environment (production vs staging API) is selected by the
// isProduction flag — it must be set before the data provider is built so the
// tenant fetch in LayoutBase hits the right environment. Self-hosted tenants
// switch by slug instead, which is resolved from the `tenant` query param in
// LayoutBase, so `mode` is a no-op for them (isProduction is ignored there).
const modeParam = new URLSearchParams(window.location.search).get('mode')
if (modeParam === 'live' || modeParam === 'test') {
	const currentWll = JSON.parse(localStorage.getItem('wll') || '{}')
	currentWll.isProduction = modeParam === 'live'
	localStorage.setItem('wll', JSON.stringify(currentWll))

	// Remove the mode parameter from the URL so it doesn't interfere with manual environment switches
	const url = new URL(window.location.href)
	url.searchParams.delete('mode')
	window.history.replaceState({}, '', url.pathname + url.search + url.hash)
}

// Consoles that target the production APIs. preprod serves the same build as
// staging but mirrors the production console's behaviour; every other host
// (staging, previews, local) targets staging.
const PRODUCTION_CONSOLE_HOSTNAMES = ['console.wlloyalty.net', 'preprod.console.wlloyalty.net']
export const isProductionConsole = PRODUCTION_CONSOLE_HOSTNAMES.includes(window.location.hostname)

// If isProduction hasn't been explicitly set, default based on the hosting domain
const currentWll = JSON.parse(localStorage.getItem('wll') || '{}')
if (currentWll.isProduction === undefined || currentWll.isProduction === null) {
	currentWll.isProduction = isProductionConsole
	localStorage.setItem('wll', JSON.stringify(currentWll))
}

const {
	isProduction = false,
}: {
	isProduction: boolean | undefined
} = JSON.parse(localStorage.getItem('wll')!) || {}

export const useLiveData = process.env.NODE_ENV === 'production' || process.env.VITE_REACT_APP_USE_LIVE_DATA === '1'

export const production = config.production
export const staging = config.staging

export default useLiveData ? (isProduction ? config.production : config.staging) : config.development

export const environment = useLiveData ? (isProduction ? 'production' : 'staging') : undefined

/**
 * The environment a tenant search/select should target, derived from the
 * console deployment (hostname) rather than the active tenant's `isProduction`.
 *
 * Tenant discovery must be stable per console: the production consoles
 * (console.wlloyalty.net, preprod.console.wlloyalty.net) always search
 * production tenants, and any other console (staging, previews) always searches
 * staging tenants — regardless of which tenant is currently active. This
 * prevents the search environment from drifting as the user switches tenants,
 * which otherwise makes tenants in the other environment unreachable.
 *
 * When not using live data (local dev), this is `undefined` so requests hit the
 * development API.
 */
export const consoleEnv: 'production' | 'staging' | undefined = useLiveData
	? isProductionConsole
		? 'production'
		: 'staging'
	: undefined

export const hotJarConfig = {
	development: {
		id: '2666481',
	},
	production: {
		id: '6405679',
	},
	staging: {
		id: '6405674',
	},
}
